import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FloatLabelModule } from 'primeng/floatlabel';
import { ToastModule } from 'primeng/toast';
import { NotificationService } from '../../../services/notification.service';

import { FiledropComponent } from '../../filedrop/filedrop.component';
import { IhkEducationVoucherService } from 'src/app/services/ihkeducationvoucher.service';
import { UserService } from 'src/app/services/user.service';
import {
  IhkEducationVoucher,
  VoucherType,
} from 'src/app/models/ihkeducationvoucher.model';
import { user } from 'src/app/models/user.model';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { AuthService } from 'src/app/services/auth.service';
import { ActivityService } from 'src/app/services/activity.service';

import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { EditorModule } from 'primeng/editor';

import { DialogManagerService } from 'src/app/services/dialog-manager.service';
import { DialogResult } from 'src/app/workflow/models/dialog-result.model';
import { ListItem } from 'src/app/models/ListItem.model';
import { ListSettingsService } from 'src/app/services/listsettings.service';
import { catchError, Observable, of, tap } from 'rxjs';

@Component({
  standalone: true,
  selector: 'app-create-ihkeducationvoucher-dialog',
  templateUrl: './create-ihkeducationvoucher-dialog.component.html',
  styleUrls: ['./create-ihkeducationvoucher-dialog.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    DialogModule,
    DropdownModule,
    ButtonModule,
    InputTextModule,
    FloatLabelModule,
    FiledropComponent,
    ToastModule,
    EditorModule,
  ],
  providers: [MessageService],
})

// HIER ARBEITEN!!!
// vouchterTypeOptions ersetzen! Async laden aus ListSettingsService, wie in DocumentsDialogComponent
export class CreateIhkEducationVoucherDialogComponent implements OnInit {
  @ViewChild('drop') fileDrop!: FiledropComponent;

  @Input() dialogVisible = false;
  @Output() dialogVisibleChange = new EventEmitter<boolean>();

  selectedUser?: user;

  voucherForm!: FormGroup;
  users: user[] = [];
  oldFiles: string[] = [];
  // Für die Async-Optionen aus ListSettingsService
  voucherTypeOptions$!: Observable<ListItem[]>;
  // Lokale Variable, um die geladenen Optionen zu speichern und im Template zu verwenden
  private voucherTypeOptions: ListItem[] = [];

  uploadInProgress = false;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private voucherSrv: IhkEducationVoucherService,
    private userSrv: UserService,
    private msg: MessageService,
    private authService: AuthService,
    private activityService: ActivityService,
    private notificationService: NotificationService,
    private ref: DynamicDialogRef,
    private dialogManager: DialogManagerService,
    private listSettingsSrv: ListSettingsService,
    public config: DynamicDialogConfig,
  ) {
    this.selectedUser = this.config.data?.selectedUser;
  }

  ngOnInit(): void {
    this.initForm();

    //  (Async Pipe im Template) Optionen aus ListSettingsService laden
    this.voucherTypeOptions$ = this.listSettingsSrv.getVoucherStyles().pipe(
      tap((data) => {
        this.voucherTypeOptions = data; // Cache für updateTitle behalten
        const currentType = this.voucherForm.get('voucherType')?.value;
        if (currentType) this.updateTitle(currentType);
      }),
      catchError(() => {
        this.msg.add({
          severity: 'error',
          summary: 'Fehler',
          detail: 'Optionen konnten nicht geladen werden',
        });
        return of([]); // Leere Liste zurückgeben, damit Dropdown nicht abstürzt
      }),
    );

    // Auf Änderungen des Typs reagieren
    this.voucherForm.get('voucherType')?.valueChanges.subscribe((type) => {
      this.updateTitle(type);
    });

    // User laden für Dropdown, wie gehabt
    this.userSrv.getUsersObservable().subscribe((users) => {
      this.users = users.filter((u) => u.roles.some((r) => r.roleId === 1));
    });
  }

  private initForm(): void {
    this.voucherForm = this.fb.group({
      titel: [''],
      userId: [this.selectedUser?.userId || '', Validators.required],
      voucherType: ['', Validators.required],
      freitext: [''],
    });
    if (this.selectedUser) this.voucherForm.get('userId')?.disable();
  }

  // Hilfsmethode für das Titel-Mapping,
  // damit es auch bei späteren Änderungen der Optionen immer aktuell ist
  private updateTitle(type: string): void {
    if (!type || this.voucherTypeOptions.length === 0) return;

    const today = new Date().toLocaleDateString('de-DE'); // Hilfsmethode oder direkt
    const selectedOption = this.voucherTypeOptions.find(
      (opt) => opt.value === type,
    );
    // Falls der Typ nicht mehr in den Optionen ist, Titel nicht ändern
    if (selectedOption) {
      this.voucherForm.patchValue(
        {
          titel: `${selectedOption.label} - ${today}`,
        },
        { emitEvent: false },
      );
    }
  }

  hasFiles(): boolean {
    return (
      this.fileDrop &&
      this.fileDrop['files'] &&
      this.fileDrop['files'].length > 0
    );
  }

  save(): void {
    if (this.voucherForm.invalid) {
      this.msg.add({
        severity: 'warn',
        summary: 'Ungültig',
        detail: 'Pflichtfelder fehlen',
      });
      return;
    }

    if (this.fileDrop['files'].length === 0 && this.oldFiles.length === 0) {
      this.msg.add({
        severity: 'warn',
        summary: 'Datei fehlt',
        detail: 'Bitte Datei hochladen.',
      });
      return;
    }
    this.saving = true;
    this.fileDrop.requestUpload();
  }

  onFilesUploaded(fileUrls: string[]): void {
    const f = this.voucherForm.getRawValue();

    const dto: IhkEducationVoucher = {
      userId: this.selectedUser ? this.selectedUser.userId : f.userId,
      voucherType: f.voucherType as VoucherType,
      titel: f.titel,
      freitext: f.freitext,
      fileUrl: fileUrls[0],
      createdBy: this.authService.getCurrentUserId(),
    };

    this.voucherSrv.createVoucher(dto).subscribe({
      next: (ref) => {
        this.activityService.getActivityById(ref.activityId).subscribe({
          next: (fullActivity) => {
            this.activityService.addActivity(fullActivity);
            this.notificationService.success(
              `Das Dokument wurde erfolgreich hochgeladen.`,
            );

            // this.ref.close(fullActivity);
            this.closeDialog({
              success: true,
              activityId: fullActivity.activityId,
              activityType: f.voucherType,
            });
          },
          error: () => {
            this.notificationService.error(
              `Aktivität erstellt, aber die Liste konnte nicht aktualisiert werden. Aktualisieren Sie die Seite.`,
            );
            this.closeDialog();
          },
        });
      },
      error: (err) => {
        this.msg.add({
          severity: 'error',
          summary: 'Fehler',
          detail: err.message,
        });
        this.saving = false;
      },
    });
  }

  closeDialog(result?: DialogResult): void {
    this.dialogVisible = false;
    this.dialogVisibleChange.emit(this.dialogVisible);
    this.voucherForm.reset();
    this.fileDrop['files'] = [];
    this.oldFiles = [];
    this.saving = false;
    this.uploadInProgress = false;
    return this.dialogManager.closeIHKEducationvoucherDialog(result);
    //this.ref.close();
  }

  isInvalid(ctrl: string): boolean {
    const c = this.voucherForm.get(ctrl);
    return !!(c && c.invalid && (c.dirty || c.touched));
  }

  getTodayDate(): string {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, '0');
    const month = (today.getMonth() + 1).toString().padStart(2, '0');
    const year = today.getFullYear();
    return `${day}.${month}.${year}`;
  }
}
